/** Readable labels: vendor_name → vendor name */
export function humanizePivotDisplay(text) {
  if (text == null || text === "") return "";
  return String(text).replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

function quotePivotValue(value) {
  const s = humanizePivotDisplay(String(value));
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function formatPivotColumnClause(column, values) {
  const col = humanizePivotDisplay(column);
  const vals = (Array.isArray(values) ? values : values != null ? [values] : [])
    .map((v) => String(v).trim())
    .filter((v) => v !== "");
  if (!vals.length) return null;
  if (vals.length === 1) {
    return `${col} = ${quotePivotValue(vals[0])}`;
  }
  const inner = vals
    .map((v) => `${col} = ${quotePivotValue(v)}`)
    .join(" OR ");
  return `(${inner})`;
}

/**
 * Same-column values → OR; different columns → AND.
 * e.g. (vendorName = "X") AND (Questions = "q1" OR Questions = "q2")
 */
export function formatWelcomePivotFilterBooleanExpr(pivotFilter) {
  if (!pivotFilter || typeof pivotFilter !== "object" || Array.isArray(pivotFilter)) {
    return "";
  }
  const clauses = Object.entries(pivotFilter)
    .map(([col, vals]) => formatPivotColumnClause(col, vals))
    .filter(Boolean);
  if (!clauses.length) return "";
  if (clauses.length === 1) return clauses[0];
  return clauses
    .map((clause) =>
      clause.startsWith("(") && clause.endsWith(")") ? clause : `(${clause})`,
    )
    .join(" AND ");
}

export function welcomePivotValueList(widget) {
  const raw = widget?.pivot_values;
  return Array.isArray(raw)
    ? raw
        .filter((v) => v != null && String(v).trim() !== "")
        .map((v) => String(v).trim())
    : raw != null && String(raw).trim() !== ""
      ? [String(raw).trim()]
      : [];
}

export function normalizeWelcomePivotFilterDict(raw) {
  if (raw == null) return null;
  let source = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      source = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }
  const out = {};
  Object.entries(source).forEach(([key, vals]) => {
    const col = key != null ? String(key).trim() : "";
    if (!col) return;
    const list = Array.isArray(vals) ? vals : vals != null ? [vals] : [];
    const cleaned = list
      .map((v) => String(v).trim())
      .filter((v) => v !== "");
    if (cleaned.length) out[col] = cleaned;
  });
  return Object.keys(out).length ? out : null;
}

export function mergeWelcomePivotFilterDicts(...filters) {
  const out = {};
  filters.forEach((pf) => {
    if (!pf) return;
    Object.entries(pf).forEach(([key, vals]) => {
      const list = Array.isArray(vals) ? vals : vals != null ? [vals] : [];
      const cleaned = list
        .map((v) => String(v).trim())
        .filter((v) => v !== "");
      if (!cleaned.length) return;
      if (!out[key]) out[key] = [];
      cleaned.forEach((v) => {
        if (!out[key].includes(v)) out[key].push(v);
      });
    });
  });
  return Object.keys(out).length ? out : null;
}

export function welcomePivotFilterFromWidget(widget) {
  const fromFilter = normalizeWelcomePivotFilterDict(widget?.pivot_filter);
  const key =
    widget?.pivot_key != null ? String(widget.pivot_key).trim() : "";
  const vals = welcomePivotValueList(widget);
  const fromLegacy = key && vals.length ? { [key]: vals } : null;
  return mergeWelcomePivotFilterDicts(fromFilter, fromLegacy);
}

export function resolveWelcomeWidgetPivotFilter(widgetRow, widgetInfo) {
  return mergeWelcomePivotFilterDicts(
    welcomePivotFilterFromWidget(widgetRow),
    welcomePivotFilterFromWidget({
      pivot_filter: widgetInfo?.pivot_filter,
      pivot_key: widgetInfo?.["Pivot column id"],
      pivot_values: widgetInfo?.["Pivot values"],
    }),
  );
}
